/**
 * cart-form-post.js
 *
 * Shared native-form cart submission. Exposed as window.MVQCartForm.
 *
 * Everything that adds to the cart in this theme does it as a real form POST to
 * /cart/add rather than a fetch to /cart/add.js. The reason is Shopify's
 * Cloudflare layer: a scripted cart request — especially one landing moments
 * after another cart request — gets challenged, the challenge answers
 * text/html, and the customer is left with a dead-end error and a half-filled
 * cart. A native form submission is a top-level document request, byte-for-byte
 * what a no-JS theme sends, so there is nothing for the bot protection to
 * challenge. `return_to` handles the post-add redirect that the fetch response
 * used to trigger.
 *
 * Two body shapes, because Shopify's /cart/add accepts either:
 *
 *   single: true   id / quantity / properties[Key]
 *                  One line item, and the ONLY shape that is well-established
 *                  for file uploads. Use it whenever files are attached.
 *
 *   single: false  items[0][id] / items[0][quantity] / items[0][properties][Key]
 *                  Any number of line items, text properties only. Already
 *                  proven in production here — it's the shape
 *                  design-placement-block.liquid posts as its bot-challenge
 *                  fallback.
 *
 * No build step in this theme, so this is a classic script exposing a global
 * rather than a module. Load it before any consumer.
 */

(function () {
  'use strict';

  // ── Field builders ─────────────────────────────────────────────────────────
  //
  // Both are pure and return [[name, value], …] for the caller to turn into
  // hidden inputs — which is what makes the request body unit-testable without
  // a DOM or a mocked fetch. Text properties only; file properties need a real
  // <input type="file">, so submitCartForm appends those itself.

  // items: [{ id, quantity, properties }]
  function buildCartFields(items, returnTo) {
    const fields = [];
    (items || []).forEach((item, i) => {
      fields.push([`items[${i}][id]`,       String(item.id)]);
      fields.push([`items[${i}][quantity]`, String(item.quantity)]);
      Object.entries(item.properties || {}).forEach(([k, v]) => {
        fields.push([`items[${i}][properties][${k}]`, String(v)]);
      });
    });
    if (returnTo) fields.push(['return_to', returnTo]);
    return fields;
  }

  // item: { id, quantity, properties }
  function buildSingleCartFields(item, returnTo) {
    const fields = [];
    if (item) {
      fields.push(['id',       String(item.id)]);
      fields.push(['quantity', String(item.quantity)]);
      Object.entries(item.properties || {}).forEach(([k, v]) => {
        fields.push([`properties[${k}]`, String(v)]);
      });
    }
    if (returnTo) fields.push(['return_to', returnTo]);
    return fields;
  }

  // ── Submission ─────────────────────────────────────────────────────────────

  // opts:
  //   items    — array of line items (or a single item when `single` is set)
  //   files    — [{ key, file, filename }] attached to the single line item.
  //              Only valid with single: true.
  //   returnTo — where Shopify sends the browser afterwards
  //   single   — use the id/quantity/properties[…] shape
  //   action   — defaults to /cart/add
  function submitCartForm(opts) {
    const o = opts || {};
    const files = o.files || [];

    if (files.length && !o.single) {
      // Guard rather than silently drop the files: multi-item file uploads via
      // items[i][properties][Key] are not an established Shopify shape, which
      // is exactly why the multi-size flow uploads once and stamps URLs.
      throw new Error('MVQCartForm: files require single: true');
    }

    const form = document.createElement('form');
    form.method = 'post';
    form.action = o.action || '/cart/add';
    form.style.display = 'none';
    // Harmless on a text-only POST, and required the moment a file rides along.
    form.enctype = 'multipart/form-data';

    const fields = o.single
      ? buildSingleCartFields(Array.isArray(o.items) ? o.items[0] : o.items, o.returnTo)
      : buildCartFields(o.items, o.returnTo);

    fields.forEach(([name, value]) => {
      const inp = document.createElement('input');
      inp.type  = 'hidden';
      inp.name  = name;
      inp.value = value;
      form.appendChild(inp);
    });

    files.forEach(fp => {
      // A DataTransfer is the only way to populate an <input type="file">
      // programmatically. Same technique as file-upload-block.liquid's
      // injectPropertiesIntoForm.
      try {
        const file = fp.file instanceof File
          ? fp.file
          : new File([fp.file], fp.filename, { type: fp.file?.type || 'application/octet-stream' });
        const dt = new DataTransfer();
        dt.items.add(file);
        const inp = document.createElement('input');
        inp.type  = 'file';
        inp.name  = `properties[${fp.key}]`;
        inp.style.display = 'none';
        form.appendChild(inp);
        inp.files = dt.files;
      } catch (e) {
        console.warn('[MVQCartForm] Could not attach file property', fp.key, e);
      }
    });

    document.body.appendChild(form);
    form.submit();
  }

  // Shopify honours `return_to` only as a same-origin relative path — it
  // discards an absolute URL as an open-redirect risk and falls back to /cart.
  // That bites anything redirecting to a stored window.location.href, so every
  // return_to goes through here. An empty result means "no opinion": the field
  // is omitted and Shopify picks its own destination.
  function toReturnToPath(url, origin) {
    if (!url || !origin) return '';
    try {
      const parsed = new URL(String(url), origin);
      if (parsed.origin !== origin) return '';
      return parsed.pathname + parsed.search;
    } catch (e) {
      return '';
    }
  }

  window.MVQCartForm = {
    buildCartFields:       buildCartFields,
    buildSingleCartFields: buildSingleCartFields,
    submitCartForm:        submitCartForm,
    toReturnToPath:        toReturnToPath,
  };

  // Exported for the node unit tests; a no-op in the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildCartFields, buildSingleCartFields, toReturnToPath };
  }
})();
